import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWorkflowQueryKey,
  saveWorkflowDefinitionGraphMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import { ApiError } from '../services/api/errors.js';
import { adaptMutation } from './adapt-mutation.js';
import type { WorkflowEdgeForm, WorkflowBuilderStep } from '../pages/admin/WorkflowBuilderTypes.js';

// ==========================================
// Hook
// ==========================================

/**
 * TanStack Query mutation for the atomic graph-save endpoint
 * (`PUT /workflows/{id}/graph`). Handles 400 (cycle) and 409 (version conflict)
 * by surfacing a string error instead of throwing.
 *
 * Usage: `const { saveGraph, saveGraphError, clearSaveGraphError } = useGraphSave(id)`
 */
export function useGraphSave(id: string | undefined) {
  const queryClient = useQueryClient();
  const [saveGraphError, setSaveGraphError] = useState<string | null>(null);

  const mutation = useMutation({
    ...adaptMutation(
      saveWorkflowDefinitionGraphMutation(),
      ({
        steps,
        edges,
        version,
      }: {
        steps: WorkflowBuilderStep[];
        edges: WorkflowEdgeForm[];
        version: number | undefined;
      }) => {
        if (!id) throw new Error('Workflow ID required for graph save');
        return {
          path: { id },
          body: {
            steps: steps.map((s) => ({
              clientId: s.id,
              diagramPosition: s.diagramPosition ?? null,
              name: s.name ?? undefined,
              executionMode: s.executionMode ?? undefined,
              optional: s.optional,
            })),
            edges: edges.map((e) => ({
              sourceClientId: e.sourceStepId,
              targetClientId: e.targetStepId,
              kind: e.kind,
              label: e.label ?? null,
            })),
            version,
          },
        };
      },
    ),
    onSuccess: () => {
      if (id) void queryClient.invalidateQueries({ queryKey: getWorkflowQueryKey({ path: { id } }) });
      setSaveGraphError(null);
    },
    onError: (err: Error) => {
      const msg = err.message ?? '';
      const status = err instanceof ApiError ? err.status : undefined;
      if (status === 400 || msg.includes('400')) {
        setSaveGraphError(
          'The workflow graph contains a cycle or invalid edge. Remove the circular dependency and try again.',
        );
      } else if (status === 409 || msg.includes('409')) {
        setSaveGraphError('Version conflict — the workflow was modified elsewhere. Reload and try again.');
      } else {
        setSaveGraphError('Graph save failed: ' + msg);
      }
    },
  });

  const saveGraph = useCallback(
    (steps: WorkflowBuilderStep[], edges: WorkflowEdgeForm[], version: number | undefined) =>
      mutation.mutateAsync({ steps, edges, version }),
    [mutation],
  );

  const clearSaveGraphError = useCallback(() => setSaveGraphError(null), []);

  return {
    saveGraph,
    saveGraphError,
    clearSaveGraphError,
    isSavingGraph: mutation.isPending,
  };
}
